import * as THREE from 'three';
import { resolveEntityItemWorldTransform } from './entityAnchorResolution.js';
export function resolveVisibleConnectorIntervals(args) {
    if (!args.gaps || args.gaps.itemKeys.length === 0)
        return [[0, 1]];
    if (![...args.start, ...args.end].every(Number.isFinite))
        throw new Error('Connector gap endpoints must be finite.');
    const { clearance } = args.gaps;
    if (!Number.isFinite(clearance) || clearance < 0)
        throw new Error('Connector gap clearance must be finite and nonnegative.');
    const hidden = [];
    for (const itemKey of args.gaps.itemKeys) {
        const state = args.context.itemParametricStateByKey?.[itemKey];
        const shape = state && 'shape' in state ? state.shape : undefined;
        // Removed topology can retain presentation references during structural handoff.
        const transform = resolveEntityItemWorldTransform({ itemKey, context: args.context });
        if (!transform) {
            const present = Array.from(args.context.entityItemsByCollectionKey.values())
                .some((items) => items.some((item) => item.itemKey === itemKey));
            if (present)
                throw new Error(`Connector gap cannot resolve panel transform: ${itemKey}`);
            continue;
        }
        if (shape?.shapeKind !== 'panel')
            throw new Error(`Connector gap requires panel bounds: ${itemKey}`);
        const inverse = transform.rotation.clone().invert();
        const start = new THREE.Vector3(...args.start).sub(transform.position).applyQuaternion(inverse);
        const end = new THREE.Vector3(...args.end).sub(transform.position).applyQuaternion(inverse);
        const from = [start.x, start.y, start.z];
        const to = [end.x, end.y, end.z];
        const extents = [shape.width / 2 + clearance, shape.height / 2 + clearance, clearance];
        let near = 0;
        let far = 1;
        for (let axis = 0; axis < 3; axis++) {
            const delta = to[axis] - from[axis];
            const extent = extents[axis];
            if (!Number.isFinite(extent) || extent < 0)
                throw new Error(`Invalid connector gap panel bounds: ${itemKey}`);
            if (Math.abs(delta) < 1e-12) {
                if (Math.abs(from[axis]) > extent) {
                    far = -1;
                    break;
                }
            }
            else {
                const first = (-extent - from[axis]) / delta;
                const second = (extent - from[axis]) / delta;
                near = Math.max(near, Math.min(first, second));
                far = Math.min(far, Math.max(first, second));
            }
        }
        if (far > near)
            hidden.push([near, far]);
    }
    hidden.sort((left, right) => left[0] - right[0]);
    const visible = [];
    let cursor = 0;
    for (const [from, to] of hidden) {
        if (from > cursor)
            visible.push([cursor, from]);
        cursor = Math.max(cursor, to);
    }
    if (cursor < 1)
        visible.push([cursor, 1]);
    return visible;
}
